<template>
  <div class="page">
    <div class="topbar">
      <div>
        <h2 class="title">{{ isManager ? 'Branch · CRM Overview' : 'Admin · CRM Overview' }}</h2>
        <div class="subtitle">
          See what’s overdue, what’s stuck, and who needs “motivation”.
        </div>
      </div>

      <div class="topbar__right">
        <button class="btn secondary" @click="loadAll" :disabled="loading">
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters">
      <select
        v-if="isAdmin"
        v-model="branchId"
        @change="onBranchChange"
        class="input"
        style="width:180px"
      >
        <option value="">All Branches</option>
        <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
      </select>

      <select
        v-if="isAdmin"
        v-model="userId"
        @change="loadAll"
        class="input"
        style="width:240px"
      >
        <option value="">All Users</option>
        <option v-for="u in users" :key="u.id" :value="u.id">
          {{ u.name }} ({{ u.id }})
        </option>
      </select>

      <span v-else-if="isManager" class="pill">
        {{ userBranchName }}
      </span>

      <input
        class="input"
        v-model="q"
        placeholder="Search name/phone/code/id"
        style="margin-left:auto;max-width:420px;"
      >
    </div>

    <!-- KPI cards -->
    <div class="kpis">
      <div class="kpi">
        <div class="kpi__label">Overdue total</div>
        <div class="kpi__value" :style="{color: overdueAll.length ? '#dc2626' : '#111827'}">
          {{ overdueAll.length }}
        </div>
        <div class="kpi__sub">Follow-ups + Docs + Quotations + Stuck orders</div>
      </div>

      <div class="kpi">
        <div class="kpi__label">Overdue follow-ups</div>
        <div class="kpi__value" :style="{color: overdueFollowups.length ? '#dc2626' : '#111827'}">
          {{ overdueFollowups.length }}
        </div>
        <div class="kpi__sub">Uncompleted past due date</div>
      </div>

      <div class="kpi">
        <div class="kpi__label">Docs overdue</div>
        <div class="kpi__value" :style="{color: docsOverdue.length ? '#dc2626' : '#111827'}">
          {{ docsOverdue.length }}
        </div>
        <div class="kpi__sub">Inquiry docs SLA breached</div>
      </div>

      <div class="kpi">
        <div class="kpi__label">Quotation overdue</div>
        <div class="kpi__value" :style="{color: quotationOverdue.length ? '#dc2626' : '#111827'}">
          {{ quotationOverdue.length }}
        </div>
        <div class="kpi__sub">Docs OK but no quote started</div>
      </div>

      <div class="kpi">
        <div class="kpi__label">Stuck orders</div>
        <div class="kpi__value" :style="{color: stuckOrders.length ? '#dc2626' : '#111827'}">
          {{ stuckOrders.length }}
        </div>
        <div class="kpi__sub">No update for {{ ORDER_STUCK_DAYS }}+ days</div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button class="tab" :class="{active: tab==='queues'}" @click="tab='queues'">
        Work queues
        <span class="chip" :class="overdueAll.length ? 'chip--red' : 'chip--green'">{{ overdueAll.length }}</span>
      </button>

      <button class="tab" :class="{active: tab==='leads'}" @click="tab='leads'">
        Leads
        <span class="chip chip--muted">{{ filteredLeads.length }}</span>
      </button>

      <button class="tab" :class="{active: tab==='inquiries'}" @click="tab='inquiries'">
        Inquiries
        <span class="chip chip--muted">{{ inquiriesFromLeads.length }}</span>
      </button>

      <button class="tab" :class="{active: tab==='missing_docs'}" @click="tab='missing_docs'">
        Missing Docs
        <span class="chip" :class="docsMissing.length ? 'chip--amber' : 'chip--green'">{{ docsMissing.length }}</span>
      </button>

      <button class="tab" :class="{active: tab==='orders'}" @click="tab='orders'">
        Orders
        <span class="chip chip--muted">{{ ordersView.length }}</span>
      </button>

      <button v-if="isAdmin" class="tab" :class="{active: tab==='people'}" @click="tab='people'">
        People performance
        <span class="chip chip--muted">{{ users.length }}</span>
      </button>
    </div>

    <!-- WORK QUEUES -->
    <div v-if="tab==='queues'" class="grid">
      <!-- Overdue unified -->
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">Overdue</div>
            <div class="card__subtitle">Everything overdue in one place</div>
          </div>
          <div class="right">
            <span class="badge" :class="overdueAll.length ? 'badge--red' : 'badge--green'">{{ overdueAll.length }}</span>
          </div>
        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="overdueAll.length===0" class="empty">
            Nothing overdue.
          </div>

          <div v-else>
            <div class="queueActions">
              <button class="btn tiny secondary" @click="selectAllOverdue(true)" :disabled="!overdueAll.length">Select all</button>
              <button class="btn tiny secondary" @click="selectAllOverdue(false)" :disabled="!overdueAll.length">Clear</button>

              <button
                class="btn tiny"
                @click="bulkMarkFollowupsDone"
                :disabled="bulkFollowupIds.length===0"
                title="Marks only overdue follow-ups as done"
              >
                Mark follow-ups done ({{ bulkFollowupIds.length }})
              </button>

              <button
                class="btn tiny"
                @click="bulkCreateDrafts"
                :disabled="bulkQuoteLeadIds.length===0"
                title="Creates draft quotations for selected quotation-overdue inquiries"
              >
                Create drafts ({{ bulkQuoteLeadIds.length }})
              </button>
            </div>

            <div class="queueList">
              <div
                v-for="x in overdueAllSorted"
                :key="x.key"
                class="queueItem"
              >
                <label class="chk" @click.stop="">
                  <input type="checkbox" :checked="isOverdueSelected(x)" @change="toggleOverdueSelected(x)">
                </label>

                <div class="queueItem__main" @click="openLead(x.leadId)" style="cursor:pointer;">
                  <div class="queueItem__top">
                    <div class="queueItem__title">
                      {{ x.title }}
                      <span class="muted">(#{{ x.code }})</span>
                    </div>
                    <span class="tag" :class="tagClass(x.type)">{{ tagText(x.type) }}</span>
                  </div>

                  <div class="queueItem__sub">
                    <b style="color:#dc2626">{{ x.sub }}</b>
                    <span v-if="x.ownerId"> • {{ ownerName(x.ownerId) }}</span>
                    <span v-if="leadPhone(x.leadId)"> • {{ leadPhone(x.leadId) }}</span>
                  </div>

                  <!-- show followup note OR lead notes if available -->
                  <div class="queueItem__note" v-if="x.note">{{ x.note }}</div>
                  <div class="queueItem__note" v-else-if="leadNotes(x.leadId)">{{ leadNotes(x.leadId) }}</div>
                </div>

                <div class="queueItem__side">
                  <button
                    v-if="x.ownerId"
                    class="btn tiny secondary"
                    @click.stop="goMessage(x.ownerId, x.type, x.leadId)"
                    title="Message owner"
                  >
                    Message
                  </button>

                  <button
                    v-if="x.action === 'mark_followup_done'"
                    class="btn tiny"
                    @click.stop="markDoneById(x.followup)"
                  >
                    Mark done
                  </button>

                  <button
                    v-else-if="x.action === 'create_draft'"
                    class="btn tiny"
                    @click.stop="createDraftForInquiry(x.leadId)"
                  >
                    Create draft
                  </button>

                  <button
                    v-else
                    class="btn tiny secondary"
                    @click.stop="openLead(x.leadId)"
                  >
                    Open
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Today panel -->
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">Due today</div>
            <div class="card__subtitle">Follow-ups due today</div>
          </div>
          <div class="right">
            <span class="badge" :class="dueToday.length ? 'badge--amber' : 'badge--green'">{{ dueToday.length }}</span>
          </div>
        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="dueToday.length===0" class="empty">Nothing due today.</div>

          <div v-else>
            <div v-for="f in dueToday" :key="f.id" class="simpleRow">
              <div class="simpleRow__main" @click="openLead(f.leadId)" style="cursor:pointer;">
                <div class="simpleRow__title">
                  {{ leadName(f.leadId) }}
                  <span class="muted">(#{{ leadCode(f.leadId) }})</span>
                </div>
                <div class="muted">
                  Due: <b>{{ fmtDate(f.dueDate) }}</b>
                  <span v-if="f.userId"> • {{ ownerName(f.userId) }}</span>
                </div>
                <div class="simpleRow__note" v-if="f.note">{{ f.note }}</div>
              </div>
              <div class="simpleRow__side">
                <button
                  v-if="f.userId"
                  class="btn tiny secondary"
                  @click.stop="goMessage(f.userId, 'followup', f.leadId)"
                  title="Message owner"
                  style="margin-right:8px;"
                >
                  Message
                </button>
                <button class="btn tiny" @click.stop="markDoneById(f)">Mark done</button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- LEADS -->
    <div v-else-if="tab==='leads'">
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">Leads</div>
            <div class="card__subtitle">All non-matured leads</div>
          </div>

          <div class="right">
            <div class="smallMuted">Click a row to open detail</div>
          </div>
        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="filteredLeads.length===0" class="muted">No leads found.</div>

          <table v-else class="table">
            <thead>
              <tr>
                <th class="sort" @click="setSort('leads','id')">ID <span class="arrow">{{ sortArrow('leads','id') }}</span></th>
                <th class="sort" @click="setSort('leads','leadCode')">Code <span class="arrow">{{ sortArrow('leads','leadCode') }}</span></th>
                <th class="sort" @click="setSort('leads','name')">Name <span class="arrow">{{ sortArrow('leads','name') }}</span></th>
                <th class="sort" @click="setSort('leads','phone')">Phone <span class="arrow">{{ sortArrow('leads','phone') }}</span></th>
                <th class="sort" @click="setSort('leads','status')">Status <span class="arrow">{{ sortArrow('leads','status') }}</span></th>
                <th class="sort" @click="setSort('leads','userId')">Owner <span class="arrow">{{ sortArrow('leads','userId') }}</span></th>
                <th class="sort" @click="setSort('leads','followUpDate')">Follow-up <span class="arrow">{{ sortArrow('leads','followUpDate') }}</span></th>
                <th>Notes</th>
                <th class="sort" @click="setSort('leads','updatedAt')">Updated <span class="arrow">{{ sortArrow('leads','updatedAt') }}</span></th>
                <th>Msg</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in sortedLeads"
                :key="l.id"
                class="rowClick"
                @click="openLead(l.id)"
                title="Open detail"
              >
                <td>{{ l.id }}</td>
                <td>{{ l.leadCode || '—' }}</td>
                <td class="primaryCell">{{ l.name || '—' }}</td>
                <td>{{ l.phone || '—' }}</td>
                <td>{{ l.status || '—' }}</td>
                <td>{{ ownerName(l.userId) }}</td>
                <td>{{ fmtDate(l.followUpDate) }}</td>
                <td @click.stop="">
                  <span class="notesCell" :title="leadNotes(l.id)">{{ truncate(leadNotes(l.id), 70) || '—' }}</span>
                </td>
                <td>{{ fmtDT(l.updatedAt) }}</td>
                <td @click.stop="">
                  <button
                    v-if="l.userId"
                    class="btn tiny secondary"
                    @click.stop="goMessage(l.userId, 'lead', l.id)"
                  >Message</button>
                  <span v-else class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- INQUIRIES -->
    <div v-else-if="tab==='inquiries'">
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">Inquiries</div>
            <div class="card__subtitle">Leads with status = matured</div>
          </div>
          <div class="right">
            <div class="smallMuted">Click a row to open detail</div>
          </div>
        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="inquiriesFromLeads.length===0" class="muted">No inquiries found.</div>

          <table v-else class="table">
            <thead>
              <tr>
                <th class="sort" @click="setSort('inquiries','id')">ID <span class="arrow">{{ sortArrow('inquiries','id') }}</span></th>
                <th class="sort" @click="setSort('inquiries','leadCode')">Code <span class="arrow">{{ sortArrow('inquiries','leadCode') }}</span></th>
                <th class="sort" @click="setSort('inquiries','name')">Name <span class="arrow">{{ sortArrow('inquiries','name') }}</span></th>
                <th class="sort" @click="setSort('inquiries','userId')">Owner <span class="arrow">{{ sortArrow('inquiries','userId') }}</span></th>
                <th class="sort" @click="setSort('inquiries','convertedAt')">Converted <span class="arrow">{{ sortArrow('inquiries','convertedAt') }}</span></th>
                <th>Docs</th>
                <th>Quote started</th>
                <th>Notes</th>
                <th>Msg</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in sortedInquiries"
                :key="l.id"
                class="rowClick"
                @click="openLead(l.id)"
              >
                <td>{{ l.id }}</td>
                <td>{{ l.leadCode || '—' }}</td>
                <td class="primaryCell">{{ l.name || '—' }}</td>
                <td>{{ ownerName(l.userId) }}</td>
                <td>{{ fmtDT(l.convertedAt) }}</td>
                <td @click.stop="">
                  <span v-if="docsMap && docsMap[String(l.id)]">
                    <span class="pillSmall" :class="docsMap[String(l.id)].docsOk ? 'ok' : 'bad'">
                      {{ docsMap[String(l.id)].docsOk ? 'OK' : 'Missing' }}
                    </span>
                  </span>
                  <span v-else class="muted">—</span>
                </td>
                <td @click.stop="">
                  <span class="pillSmall" :class="hasAnyQuotation(l.id) ? 'ok' : 'bad'">
                    {{ hasAnyQuotation(l.id) ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td @click.stop="">
                  <span class="notesCell" :title="leadNotes(l.id)">{{ truncate(leadNotes(l.id), 70) || '—' }}</span>
                </td>
                <td @click.stop="">
                  <button
                    v-if="l.userId"
                    class="btn tiny secondary"
                    @click.stop="goMessage(l.userId, 'inquiry', l.id)"
                  >Message</button>
                  <span v-else class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>

        </div>
      </div>
    </div>

    <!-- MISSING DOCS -->
    <div v-else-if="tab==='missing_docs'">
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">Missing Docs</div>
            <div class="card__subtitle">Inquiries missing measurements / drawings / site photos</div>
          </div>
          <div class="right">
            <span class="badge" :class="docsOverdue.length ? 'badge--red' : 'badge--green'">Overdue: {{ docsOverdue.length }}</span>
          </div>
        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="docsMissing.length===0" class="muted">No missing docs.</div>

          <table v-else class="table table--docs">
            <thead>
              <tr>
                <th class="sort" @click="setSort('docs','id')">ID <span class="arrow">{{ sortArrow('docs','id') }}</span></th>
                <th class="sort" @click="setSort('docs','name')">Name <span class="arrow">{{ sortArrow('docs','name') }}</span></th>
                <th class="sort" @click="setSort('docs','userId')">Owner <span class="arrow">{{ sortArrow('docs','userId') }}</span></th>
                <th>Measurements</th>
                <th>Drawings</th>
                <th>Site photos</th>
                <th class="sort" @click="setSort('docs','deadline')">Deadline <span class="arrow">{{ sortArrow('docs','deadline') }}</span></th>
                <th class="sort" @click="setSort('docs','overdue')">Overdue <span class="arrow">{{ sortArrow('docs','overdue') }}</span></th>
                <th>Notes</th>
                <th>Msg</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="l in sortedDocsMissing"
                :key="l.id"
                class="rowClick"
                @click="openLead(l.id)"
              >
                <td>{{ l.id }}</td>
                <td class="primaryCell">{{ l.name || '—' }}</td>
                <td>{{ ownerName(l.userId) }}</td>
                <td>{{ num(getDoc(l.id, 'measurements')) }}</td>
                <td>{{ num(getDoc(l.id, 'drawings')) }}</td>
                <td>{{ num(getDoc(l.id, 'sitePhotos')) }}</td>
                <td>{{ fmtDate(deadline(l)) }}</td>
                <td @click.stop="">
                  <span class="pillSmall" :class="isOverdue(l) ? 'bad' : 'ok'">
                    {{ isOverdue(l) ? 'Overdue' : 'On time' }}
                  </span>
                </td>
                <td @click.stop="">
                  <span class="notesCell" :title="leadNotes(l.id)">{{ truncate(leadNotes(l.id), 70) || '—' }}</span>
                </td>
                <td @click.stop="">
                  <button
                    v-if="l.userId"
                    class="btn tiny secondary"
                    @click.stop="goMessage(l.userId, 'docs', l.id)"
                  >Message</button>
                  <span v-else class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>

        </div>
      </div>
    </div>

    <!-- ORDERS -->
    <div v-else-if="tab==='orders'">
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">Orders</div>
            <div class="card__subtitle">Stage-wise pipeline + stuck alerts</div>
          </div>
          <div class="right">
            <span class="badge" :class="stuckOrders.length ? 'badge--red' : 'badge--green'">Stuck: {{ stuckOrders.length }}</span>
          </div>
        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="ordersView.length===0" class="muted">No orders found.</div>

          <div v-else>
            <div
              v-if="bmPendingOrders.length"
              style="margin-bottom:12px; padding:10px 12px; border-radius:14px; background:#fff7ed; border:1px solid #fed7aa;"
            >
              <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px;">
                <div>
                  <div style="font-weight:800; color:#9a3412;">Pending Branch Manager approval (BM_PENDING)</div>
                  <div class="muted" style="font-size:12px; margin-top:2px;">These orders are waiting for BM approval. Managers can approve/reject right here.</div>
                </div>
                <button
                  class="btn tiny secondary"
                  @click="orderStageFilter = 'BM_PENDING'"
                  :disabled="orderStageFilter === 'BM_PENDING'"
                  title="Filter to BM_PENDING"
                >
                  View ({{ bmPendingOrders.length }})
                </button>
              </div>

              <div style="margin-top:10px; overflow:auto;">
                <table class="table" style="min-width:760px;">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Lead</th>
                      <th>Owner</th>
                      <th>Updated</th>
                      <th style="width:240px;">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="o in bmPendingOrders.slice(0, 8)" :key="'bm'+o.id" class="rowClick" @click="openOrder(o.id)">
                      <td>{{ o.id }}</td>
                      <td class="primaryCell">{{ leadName(o.leadId) }}</td>
                      <td>{{ ownerName(o.userId) }}</td>
                      <td>{{ fmtDT(o.updatedAt) }}</td>
                      <td @click.stop="">
                        <button class="btn tiny secondary" @click.stop="openOrder(o.id)">Open</button>
                        <!-- <button class="btn tiny secondary" style="margin-left:8px;" @click.stop="openLead(o.leadId)">Lead</button> -->

                        <template v-if="isManager">
                          <button
                            class="btn tiny"
                            style="margin-left:8px;"
                            :disabled="bmActing[o.id]"
                            @click.stop="bmApproveOrder(o.id)"
                          >{{ bmActing[o.id] ? '...' : 'Approve' }}</button>

                          <button
                            class="btn tiny secondary"
                            style="margin-left:8px;"
                            :disabled="bmActing[o.id]"
                            @click.stop="bmRejectOrder(o.id)"
                          >Reject</button>
                        </template>

                        <button
                          v-if="o.userId"
                          class="btn tiny secondary"
                          style="margin-left:8px;"
                          @click.stop="goMessage(o.userId, 'order', o.id)"
                        >Message owner</button>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-if="bmPendingOrders.length > 8" class="muted" style="font-size:12px; margin-top:6px;">
                  Showing 8 of {{ bmPendingOrders.length }}. Use the filter to view all.
                </div>
              </div>
            </div>



            <!-- BIRD'S EYE VIEW: stage summary chips -->
            <div class="queueActions" style="margin-bottom:12px;">
              <button
                class="btn tiny secondary"
                @click="orderStageFilter = ''"
                :disabled="orderStageFilter === ''"
                title="Show all stages"
              >
                All ({{ ordersView.length }})
              </button>

              <button
                v-for="s in orderStages"
                :key="s"
                class="btn tiny secondary"
                @click="orderStageFilter = s"
                :disabled="orderStageFilter === s"
                :title="'Filter: ' + s"
              >
                {{ s }} ({{ stageCounts[s] || 0 }})
              </button>

              <span v-if="orderStageFilter" class="pill">
                Filter: {{ orderStageFilter }}
              </span>
            </div>

            <table class="table">
              <thead>
                <tr>
                  <th class="sort" @click="setSort('orders','id')">ID <span class="arrow">{{ sortArrow('orders','id') }}</span></th>
                  <th class="sort" @click="setSort('orders','leadId')">Lead <span class="arrow">{{ sortArrow('orders','leadId') }}</span></th>
                  <th class="sort" @click="setSort('orders','stage')">Stage <span class="arrow">{{ sortArrow('orders','stage') }}</span></th>
                  <th class="sort" @click="setSort('orders','updatedAt')">Updated <span class="arrow">{{ sortArrow('orders','updatedAt') }}</span></th>
                  <th class="sort" @click="setSort('orders','stuck')">Stuck <span class="arrow">{{ sortArrow('orders','stuck') }}</span></th>
                  <th class="sort" @click="setSort('orders','owner')">Owner <span class="arrow">{{ sortArrow('orders','owner') }}</span></th>
                  <th>Actions</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="o in sortedOrders"
                  :key="o.id"
                  class="rowClick"
                  @click="openOrder(o.id)"
                  title="Open order detail"
                >
                  <td>{{ o.id }}</td>
                  <td class="primaryCell">{{ leadName(o.leadId) }}</td>
                  <td>{{ o.stage || '—' }}</td>
                  <td>{{ fmtDT(o.updatedAt) }}</td>
                  <td @click.stop="">
                    <span class="pillSmall" :class="isOrderStuck(o) ? 'bad' : 'ok'">
                      {{ isOrderStuck(o) ? 'Yes' : 'No' }}
                    </span>
                  </td>
                  <td @click.stop="">
                    {{ ownerName(o.userId) }}
                  </td>
                  <td @click.stop="">
                    <button class="btn tiny secondary" @click.stop="openOrder(o.id)">Open</button>
                    <button class="btn tiny secondary" style="margin-left:8px;" @click.stop="openLead(o.leadId)">Lead</button>

                    <button
                      v-if="isManager && (o.stage === 'BM_PENDING')"
                      class="btn tiny"
                      style="margin-left:8px;"
                      :disabled="bmActing[o.id]"
                      @click.stop="bmApproveOrder(o.id)"
                    >{{ bmActing[o.id] ? '…' : 'Approve' }}</button>

                    <button
                      v-if="isManager && (o.stage === 'BM_PENDING')"
                      class="btn tiny secondary"
                      style="margin-left:8px;"
                      :disabled="bmActing[o.id]"
                      @click.stop="bmRejectOrder(o.id)"
                    >Reject</button>

                    <button
                      v-if="o.userId"
                      class="btn tiny secondary"
                      style="margin-left:8px;"
                      @click.stop="goMessage(o.userId, 'order', o.id)"
                    >Message</button>

                  </td>
                  <td @click.stop="">
                    <span class="notesCell" :title="leadNotes(o.leadId)">{{ truncate(leadNotes(o.leadId), 70) || '—' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>

    <!-- PEOPLE PERFORMANCE (Admin only) -->
    <div v-else-if="tab==='people' && isAdmin">
      <div class="card">
        <div class="card__header">
          <div>
            <div class="card__title">People performance</div>
            <div class="card__subtitle">Overdue counts by employee.</div>
          </div>
          <button
            class="btn secondary"
            style="margin-left:auto;"
            @click="showOnlyBacklog = !showOnlyBacklog"
          >
            {{ showOnlyBacklog ? 'Show all users' : 'Show only backlog' }}
          </button>

        </div>

        <div class="card__body">
          <div v-if="loading" class="muted">Loading…</div>
          <div v-else-if="peopleRows.length===0" class="muted">No one has any work backlogged at the moment!</div>

          <table v-else class="table">
            <thead>
              <tr>
                <th class="sort" @click="setSort('people','name')">User <span class="arrow">{{ sortArrow('people','name') }}</span></th>
                <th class="sort" @click="setSort('people','branch')">Branch <span class="arrow">{{ sortArrow('people','branch') }}</span></th>
                <th class="sort" @click="setSort('people','overdueFollowups')">Overdue follow-ups <span class="arrow">{{ sortArrow('people','overdueFollowups') }}</span></th>
                <th class="sort" @click="setSort('people','docsOverdue')">Docs overdue <span class="arrow">{{ sortArrow('people','docsOverdue') }}</span></th>
                <th class="sort" @click="setSort('people','quoteOverdue')">Quote overdue <span class="arrow">{{ sortArrow('people','quoteOverdue') }}</span></th>
                <th class="sort" @click="setSort('people','stuckOrders')">Stuck orders <span class="arrow">{{ sortArrow('people','stuckOrders') }}</span></th>
                <th class="sort" @click="setSort('people','total')">Total overdue <span class="arrow">{{ sortArrow('people','total') }}</span></th>
                <th>Msg</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in sortedPeople" :key="r.userId">
                <td class="primaryCell">
                  {{ r.name }}
                  <div class="muted" style="font-size:12px;">{{ r.userId }}</div>
                </td>
                <td>{{ r.branchId || '—' }}</td>
                <td><span class="pillSmall" :class="r.overdueFollowups ? 'bad' : 'ok'">{{ r.overdueFollowups }}</span></td>
                <td><span class="pillSmall" :class="r.docsOverdue ? 'bad' : 'ok'">{{ r.docsOverdue }}</span></td>
                <td><span class="pillSmall" :class="r.quoteOverdue ? 'bad' : 'ok'">{{ r.quoteOverdue }}</span></td>
                <td><span class="pillSmall" :class="r.stuckOrders ? 'bad' : 'ok'">{{ r.stuckOrders }}</span></td>
                <td><span class="pillSmall" :class="r.total ? 'bad' : 'ok'">{{ r.total }}</span></td>
                <td @click.stop="">
                  <button
                    class="btn tiny secondary"
                    @click.stop="goMessage(r.userId, 'person', r.userId)"
                  >Message</button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="muted" style="margin-top:10px;">
            Tip: Filter by branch/user above to focus on one team without staring at chaos.
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'AdminCRM',
  data(){
    return {
      tab:'queues',
      q:'',
      loading:false,

      leads:[],
      quotations:[],
      orders:[],
      followups:[],
      dashboard: null,

      branchId:'',
      branches:[],
      docsMap: {},
      users: [],
      userId: '',
      showOnlyBacklog: true,
      bmActing: {},

      // Birds-eye order filter
      orderStageFilter: '',

      // sorting per view
      sortState: {
        queues: { key: 'age', dir: 'asc' },
        leads: { key: 'updatedAt', dir: 'desc' },
        inquiries: { key: 'convertedAt', dir: 'desc' },
        docs: { key: 'overdue', dir: 'desc' },
        orders: { key: 'updatedAt', dir: 'desc' },
        people: { key: 'total', dir: 'desc' }
      },

      // overdue bulk selection
      overdueSelected: {},

      // knobs (feel free to tweak)
      QUOTATION_SLA_HOURS: 12,
      DOCS_SLA_DAYS: 1
    }
  },

  computed:{
    user(){ return this.$store.getters['auth/currentUser'] || {} },
    isAdmin(){ return this.user.role === 'super_admin' || this.user.role === 'branch_admin' },
    isManager(){ return this.user.role === 'branch_manager' },
    isBirdEye(){
      const r = this.user.role
      return r === 'branch_manager' || r === 'branch_admin' || r === 'super_admin'
    },

    bmPendingOrders(){
      // Orders currently waiting on Branch Manager approval
      return (this.ordersView || []).filter(o => String(o.stage || '').toUpperCase() === 'BM_PENDING')
    },

    userBranchName(){ return this.user.branchId || '' },

    ORDER_STUCK_DAYS(){ return 7 },

    todayISO(){
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return y + '-' + m + '-' + day
    },

    filteredLeads(){
      const t = (this.q||'').toLowerCase()
      return (this.leads||[])
        .filter(l => (l.status||'').toLowerCase() !== 'converted_to_inquiry')
        .filter(l => this.blobLead(l).indexOf(t) > -1)
    },

    inquiriesFromLeads () {
      const t = (this.q || '').toLowerCase()
      const hasOrder = this.hasOrderByLeadId || {}

      return (this.leads || [])
        .filter(l => (l.status || '').toLowerCase() === 'converted_to_inquiry')
        // ✅ HIDE inquiries that already have an order
        .filter(l => !hasOrder[String(l.id)])
        .filter(l => {
          if (!t) return true
          return this.blobLead(l).indexOf(t) > -1
        })
    },


    ordersView(){
      const t = (this.q||'').toLowerCase()
      return (this.orders||[]).filter(o => {
        const blob = String(o.id)+' '+String(o.leadId)+' '+String(o.stage||'')+' '+String(o.updatedAt||'')
        return blob.toLowerCase().indexOf(t) > -1
      })
    },
    hasOrderByLeadId () {
      const m = {}
      const list = Array.isArray(this.orders) ? this.orders : []
      for (let i = 0; i < list.length; i++) {
        const lid = list[i] && list[i].leadId ? String(list[i].leadId) : ''
        if (lid) m[lid] = true
      }
      return m
    },



    // Birds-eye: stage counts + stage list
    stageCounts(){
      const m = {}
      const list = this.ordersView || []
      for (let i=0;i<list.length;i++){
        const s = String(list[i].stage || '—').toUpperCase()
        m[s] = (m[s] || 0) + 1
      }
      return m
    },
    orderStages(){
      const keys = Object.keys(this.stageCounts || {})
      // keep "—" last
      keys.sort((a,b) => {
        if (a === '—') return 1
        if (b === '—') return -1
        return a.localeCompare(b)
      })
      return keys
    },

    docsMissing(){
      const docs = this.docsMap || {}
      return this.inquiriesFromLeads.filter(l => {
        const st = docs[String(l.id)]
        return !(st && st.docsOk)
      })
    },

    docsOverdue(){
      const now = Date.now()
      return this.docsMissing.filter(l => {
        const dl = this.deadline(l)
        return dl && now > dl.getTime()
      })
    },

    overdueFollowups(){
      const today = this.todayISO
      return (this.followups || [])
        .filter(f => Number(f.isDone) === 0)
        .filter(f => this.toYMD(f.dueDate) && this.toYMD(f.dueDate) < today)
        .sort((a,b) => this.toYMD(a.dueDate).localeCompare(this.toYMD(b.dueDate)))
    },

    dueToday(){
      const today = this.todayISO
      return (this.followups || [])
        .filter(f => Number(f.isDone) === 0)
        .filter(f => this.toYMD(f.dueDate) === today)
        .sort((a,b) => String(a.dueDate).localeCompare(String(b.dueDate)))
    },

    quotationOverdue(){
      const now = Date.now()
      const docs = this.docsMap || {}

      const hasAnyQuoteByLead = {}
      ;(this.quotations || []).forEach(function(q){
        hasAnyQuoteByLead[String(q.leadId)] = true
      })

      return (this.inquiriesFromLeads || [])
        .filter(l => {
          const st = docs[String(l.id)]
          return st && st.docsOk
        })
        .filter(l => !hasAnyQuoteByLead[String(l.id)])
        .filter(l => {
          const t = new Date(l.convertedAt || 0).getTime()
          if (!t) return false
          return (now - t) > (this.QUOTATION_SLA_HOURS * 60 * 60 * 1000)
        })
        .sort((a,b) => new Date(a.convertedAt||0).getTime() - new Date(b.convertedAt||0).getTime())
    },

    stuckOrders(){
      return (this.ordersView || []).filter(o => this.isOrderStuck(o))
    },

    overdueAll(){
      const items = []

      for (let i=0;i<(this.overdueFollowups||[]).length;i++){
        const f = this.overdueFollowups[i]
        items.push({
          type: 'followup',
          key: 'f-' + f.id,
          leadId: f.leadId,
          ownerId: f.userId,
          title: this.leadName(f.leadId),
          code: this.leadCode(f.leadId),
          sub: 'Follow-up overdue: ' + this.fmtDate(f.dueDate),
          note: f.note || '',
          action: 'mark_followup_done',
          followup: f
        })
      }

      for (let j=0;j<(this.docsOverdue||[]).length;j++){
        const l = this.docsOverdue[j]
        const st = (this.docsMap || {})[String(l.id)] || {}
        items.push({
          type: 'docs',
          key: 'd-' + l.id,
          leadId: l.id,
          ownerId: l.userId,
          title: l.name || ('Lead #' + l.id),
          code: (l.leadCode || l.id),
          sub: 'Docs overdue. Deadline: ' + this.fmtDate(this.deadline(l)),
          note: 'Photos: ' + (st.sitePhotos || 0) + ' • Measurements: ' + (st.measurements || 0),
          action: 'open'
        })
      }

      for (let k=0;k<(this.quotationOverdue||[]).length;k++){
        const l2 = this.quotationOverdue[k]
        items.push({
          type: 'quote',
          key: 'q-' + l2.id,
          leadId: l2.id,
          ownerId: l2.userId,
          title: l2.name || ('Lead #' + l2.id),
          code: (l2.leadCode || l2.id),
          sub: 'Quotation overdue. Docs OK, quote not started.',
          note: 'Converted: ' + this.fmtDT(l2.convertedAt),
          action: 'create_draft'
        })
      }

      for (let m=0;m<(this.stuckOrders||[]).length;m++){
        const o = this.stuckOrders[m]
        items.push({
          type: 'order',
          key: 'o-' + o.id,
          leadId: o.leadId,
          ownerId: o.userId,
          title: this.leadName(o.leadId),
          code: this.leadCode(o.leadId),
          sub: 'Order stuck at ' + (o.stage || '—') + ' • last update: ' + this.fmtDT(o.updatedAt),
          note: 'Order #' + o.id,
          action: 'open'
        })
      }

      return items
    },

    overdueAllSorted(){
      const arr = (this.overdueAll || []).slice(0)
      const key = (this.sortState.queues && this.sortState.queues.key) ? this.sortState.queues.key : 'age'
      const dir = (this.sortState.queues && this.sortState.queues.dir) ? this.sortState.queues.dir : 'asc'
      const mul = dir === 'desc' ? -1 : 1

      const tsFor = (x) => {
        if (!x) return 0
        if (x.type === 'followup') {
          const d = x.followup ? x.followup.dueDate : null
          const ymd = this.toYMD(d)
          if (ymd) return new Date(ymd + 'T00:00:00').getTime()
          return 0
        }
        if (x.type === 'docs' || x.type === 'quote') {
          const l = this.leadById(x.leadId)
          const t = new Date((l && l.convertedAt) || 0).getTime()
          return t || 0
        }
        if (x.type === 'order') {
          const t2 = new Date(x.updatedAt || 0).getTime()
          return t2 || 0
        }
        return 0
      }

      arr.sort((a,b) => {
        if (key === 'type') return String(a.type).localeCompare(String(b.type)) * mul
        return (tsFor(a) - tsFor(b)) * mul
      })
      return arr
    },

    bulkFollowupIds(){
      const ids = []
      const sel = this.overdueSelected || {}
      const all = this.overdueAll || []
      for (let i=0;i<all.length;i++){
        const x = all[i]
        if (x && x.type === 'followup' && sel[x.key]) {
          if (x.followup && x.followup.id) ids.push(String(x.followup.id))
        }
      }
      return ids
    },

    bulkQuoteLeadIds(){
      const ids = []
      const sel = this.overdueSelected || {}
      const all = this.overdueAll || []
      for (let i=0;i<all.length;i++){
        const x = all[i]
        if (x && x.type === 'quote' && sel[x.key]) ids.push(String(x.leadId))
      }
      return ids
    },

    // maps for people performance
    overdueFollowupsByUser(){
      const m = {}
      const list = this.overdueFollowups || []
      for (let i=0;i<list.length;i++){
        const uid = String(list[i].userId || '')
        if (!uid) continue
        m[uid] = (m[uid] || 0) + 1
      }
      return m
    },
    overdueDocsByUser(){
      const m = {}
      const list = this.docsOverdue || []
      for (let i=0;i<list.length;i++){
        const uid = String(list[i].userId || '')
        if (!uid) continue
        m[uid] = (m[uid] || 0) + 1
      }
      return m
    },
    overdueQuotesByUser(){
      const m = {}
      const list = this.quotationOverdue || []
      for (let i=0;i<list.length;i++){
        const uid = String(list[i].userId || '')
        if (!uid) continue
        m[uid] = (m[uid] || 0) + 1
      }
      return m
    },
    stuckOrdersByUser(){
      const m = {}
      const list = this.stuckOrders || []
      for (let i=0;i<list.length;i++){
        const uid = String(list[i].userId || '')
        if (!uid) continue
        m[uid] = (m[uid] || 0) + 1
      }
      return m
    },

    peopleRows(){
      const users = Array.isArray(this.users) ? this.users : []

      const overdueFollowupsByUser = this.overdueFollowupsByUser || {}
      const overdueDocsByUser = this.overdueDocsByUser || {}
      const overdueQuotesByUser = this.overdueQuotesByUser || {}
      const stuckOrdersByUser = this.stuckOrdersByUser || {}

      const rows = users.map(u => {
        const uid = String(u.id)

        const a = Number(overdueFollowupsByUser[uid] || 0)
        const b = Number(overdueDocsByUser[uid] || 0)
        const c = Number(overdueQuotesByUser[uid] || 0)
        const d = Number(stuckOrdersByUser[uid] || 0)

        return {
          userId: u.id,
          name: u.name,
          role: u.role,
          branchId: u.branchId,
          overdueFollowups: a,
          docsOverdue: b,
          quoteOverdue: c,
          stuckOrders: d,
          total: a + b + c + d,
        }
      })

      if (this.showOnlyBacklog) return rows.filter(r => r.total > 0)
      return rows.sort((x,y) => (y.total||0) - (x.total||0))
    },

    sortedPeople(){
      const arr = (this.peopleRows || []).slice(0)
      const st = this.sortState.people || { key:'total', dir:'desc' }
      const key = st.key
      const dir = st.dir === 'asc' ? 1 : -1

      arr.sort((a,b) => {
        const av = (a && a[key] !== undefined) ? a[key] : ''
        const bv = (b && b[key] !== undefined) ? b[key] : ''
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
      return arr
    },

    sortedLeads(){ return this.sortGeneric(this.filteredLeads || [], 'leads') },
    sortedInquiries(){ return this.sortGeneric(this.inquiriesFromLeads || [], 'inquiries') },

    sortedDocsMissing(){
      const list = (this.docsMissing || []).slice(0)
      const st = this.sortState.docs || { key:'overdue', dir:'desc' }
      const key = st.key
      const dir = st.dir === 'asc' ? 1 : -1

      list.sort((a,b) => {
        if (key === 'deadline') {
          const da = this.deadline(a); const db = this.deadline(b)
          const ta = da ? da.getTime() : 0
          const tb = db ? db.getTime() : 0
          return (ta - tb) * dir
        }
        if (key === 'overdue') {
          const oa = this.isOverdue(a) ? 1 : 0
          const ob = this.isOverdue(b) ? 1 : 0
          return (oa - ob) * dir
        }
        const av = (a && a[key] !== undefined) ? a[key] : ''
        const bv = (b && b[key] !== undefined) ? b[key] : ''
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
      return list
    },

    sortedOrders(){
      //birds-eye filter first then existing sorting
      const base = (this.ordersView || []).filter(o => {
        if (!this.orderStageFilter) return true
        return String(o.stage || '—').toUpperCase() === String(this.orderStageFilter).toUpperCase()
      })

      const list = base.slice(0)
      const st = this.sortState.orders || { key:'updatedAt', dir:'desc' }
      const key = st.key
      const dir = st.dir === 'asc' ? 1 : -1

      list.sort((a,b) => {
        if (key === 'stuck') {
          const sa = this.isOrderStuck(a) ? 1 : 0
          const sb = this.isOrderStuck(b) ? 1 : 0
          return (sa - sb) * dir
        }
        if (key === 'updatedAt') {
          const ta = new Date(a.updatedAt || 0).getTime()
          const tb = new Date(b.updatedAt || 0).getTime()
          return (ta - tb) * dir
        }
        const av = (a && a[key] !== undefined) ? a[key] : ''
        const bv = (b && b[key] !== undefined) ? b[key] : ''
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
      return list
    }
  },

  created(){ this.loadAll() },

  methods:{
    openLead(id){
      if (!id) return
      this.$router.push({ name:'sales-customer-detail', params:{ id: id } })
    },
    openOrder(id){
      if (!id) return
      this.$router.push({ name:'sales-order-detail', params:{ id: id } })
    },

    async bmApproveOrder(orderId){
      if (!orderId) return
      if (!this.isManager) return
      if (this.bmActing[orderId]) return
      this.$set(this.bmActing, orderId, true)
      try {
        await this.$api.post(`/orders/${orderId}/bm/approve`)
        await this.loadAll()
      } catch(e){
        console.error(e)
        alert('Could not approve. Please refresh and try again.')
      } finally {
        this.$set(this.bmActing, orderId, false)
      }
    },

    async bmRejectOrder(orderId){
      if (!orderId) return
      if (!this.isManager) return
      const note = window.prompt('Why are you rejecting this order? (This will be sent to the owner)', '')
      if (!note || !String(note).trim()) return
      if (this.bmActing[orderId]) return
      this.$set(this.bmActing, orderId, true)
      try {
        await this.$api.post(`/orders/${orderId}/bm/reject`, { note: String(note).trim() })
        await this.loadAll()
      } catch(e){
        console.error(e)
        alert('Could not reject. Please refresh and try again.')
      } finally {
        this.$set(this.bmActing, orderId, false)
      }
    },


    leadById(id){
      return (this.leads || []).find(l => String(l.id)===String(id)) || null
    },

    ownerName(uid){
      const u = (this.users || []).find(x => String(x.id) === String(uid))
      return u ? u.name : (uid || '—')
    },

    leadName(leadId){
      const l = (this.leads||[]).find(x => String(x.id) === String(leadId))
      return l ? (l.name || ('Lead #' + leadId)) : ('Lead #' + leadId)
    },

    leadCode(leadId){
      const l = this.leadById(leadId)
      return l ? (l.leadCode || l.id) : leadId
    },

    leadPhone(leadId){
      const l = this.leadById(leadId)
      return l ? (l.phone || '') : ''
    },

    leadNotes(leadId){
      const l = this.leadById(leadId)
      const n = l ? (l.notes || '') : ''
      return String(n || '').trim()
    },

    truncate(s, max){
      const t = String(s || '')
      const m = Number(max || 0) || 60
      if (!t) return ''
      if (t.length <= m) return t
      return t.slice(0, m - 1) + '…'
    },

    blobLead(l){
      const blob =
        String(l.id)+' '+
        String(l.leadCode||'')+' '+
        String(l.name || '')+' '+
        String(l.phone||'')+' '+
        String(l.email||'')+' '+
        String(l.notes||'')+' '+
        String(l.address||'')
      return blob.toLowerCase()
    },

    onBranchChange(){
      this.userId = ''
      this.loadAll()
    },

    async loadAll(){
      this.loading = true
      try{
        const api = this.$api

        // Keep these only for admin filters / messaging name lookup
        if (this.isAdmin && (!this.branches || !this.branches.length)) {
          const br = await api.get('/branches')
          this.branches = Array.isArray(br.data) ? br.data : []
        }
        if ((this.isAdmin || this.isManager) && (!this.users || !this.users.length)) {
          const us = await api.get('/users')
          this.users = Array.isArray(us.data) ? us.data : []
        }

        // Admin-only filters (backend currently ignores these in your endpoint,
        // but we keep the params ready if you later add support)
        const params = {}
        if (this.isAdmin) {
          if (this.branchId) params.branchId = this.branchId
          if (this.userId) params.userId = this.userId
          // DO NOT send scope=all unless your backend explicitly supports it
        }

        const { data } = await api.get('/dashboard/sales', { params })
        this.dashboard = data || {}

        // 1) Followups (normalize to your UI followup shape)
        this.followups = this.normalizeFollowups(this.dashboard.leadsFollowupDue || [])

        // 2) Orders (merge buckets + normalize)
        const ob = (this.dashboard.orders || {})
        const mergedOrders = []
          .concat(Array.isArray(ob.bmPending) ? ob.bmPending : [])
          .concat(Array.isArray(ob.bmRejected) ? ob.bmRejected : [])
          .concat(Array.isArray(ob.active) ? ob.active : [])
        this.orders = this.normalizeOrders(mergedOrders)

        // 3) Build a "lead-like" list for tables/search/openLead/notes/phone
        // We synthesize leads from all dashboard buckets to keep your UI alive.
        this.leads = this.buildLeadsFromDashboard(this.dashboard)

        // 4) Docs map (leadId keyed, because your UI uses lead.id everywhere)
        this.docsMap = this.buildDocsMapFromDashboard(this.dashboard)

        // 5) Quotations (minimal leadId presence so hasAnyQuotation() works)
        this.quotations = this.buildQuotationsFromDashboard(this.dashboard)

      } catch(e){
        console.error('AdminCRM loadAll failed', e)
      } finally {
        this.loading = false
      }
    },

    normalizeFollowups(list){
      // dashboard returns leads list (not followups table), so we adapt:
      // expected by UI: { id, leadId, dueDate, isDone, userId, note }
      return (Array.isArray(list) ? list : []).map(x => ({
        id: x.id || (x.leadId ? ('lead-'+x.leadId) : Math.random().toString(36).slice(2)),
        leadId: x.id || x.leadId,                // dashboard A returns l.id as id
        dueDate: x.followUpDate || x.follow_up_date || null,
        isDone: 0,
        userId: x.userId || x.ownerUserId || x.owner_user_id || null,
        note: ''                                  // dashboard doesn’t include followup note
      }))
    },

    normalizeOrders(list){
      return (Array.isArray(list) ? list : []).map(o => ({
        id: o.id,
        leadId: o.leadId || o.lead_id || null,
        stage: o.stage || null,
        updatedAt: o.updatedAt || o.updated_at || o.createdAt || o.created_at || null,
        userId: o.userId || o.ownerUserId || o.owner_user_id || null,
        bmNote: o.bmNote || o.bm_note || null,
      }))
    },

    buildLeadsFromDashboard(d){
      // Create unique lead records keyed by leadId.
      // Used by leadName/leadPhone/leadNotes/openLead etc.
      const map = {}

      const addLead = (leadId, payload) => {
        if (!leadId) return
        const k = String(leadId)
        if (!map[k]) map[k] = { id: leadId }
        map[k] = Object.assign(map[k], payload || {})
      }

      // A) leadsFollowupDue already contains lead fields
      ;(Array.isArray(d.leadsFollowupDue) ? d.leadsFollowupDue : []).forEach(l => {
        addLead(l.id, {
          leadCode: l.leadCode,
          name: l.name,
          phone: l.phone,
          email: l.email,
          followUpDate: l.followUpDate,
          userId: l.userId,
          branchId: l.branchId,
          status: 'follow_up',
          createdAt: l.createdAt,
          updatedAt: l.updatedAt,
          notes: ''
        })
      })

      // B) inquiry buckets (use leadId)
      const addInquiryLead = (x, statusOverride) => {
        addLead(x.leadId, {
          leadCode: x.leadCode,
          name: x.customerName,
          phone: x.customerPhone,
          userId: x.ownerUserId || x.owner_user_id,
          branchId: x.branchId,
          status: statusOverride || 'converted_to_inquiry',
          convertedAt: x.createdAt || x.docsCompletedAt || null,
          updatedAt: x.createdAt || null,
          notes: ''
        })
      }

      ;(Array.isArray(d.inquiriesOverdue) ? d.inquiriesOverdue : []).forEach(x => addInquiryLead(x, 'converted_to_inquiry'))
      ;(Array.isArray(d.inquiriesDocsPending) ? d.inquiriesDocsPending : []).forEach(x => addInquiryLead(x, 'converted_to_inquiry'))
      ;(Array.isArray(d.readyForQuotation) ? d.readyForQuotation : []).forEach(x => addInquiryLead(x, 'converted_to_inquiry'))

      // C) quotation action needed (leadId)
      const qa = d.quoteActionNeeded || {}
      const addQuoteLead = (x) => {
        addLead(x.leadId, {
          leadCode: x.leadCode,
          name: x.customerName,
          phone: x.customerPhone,
          userId: x.ownerUserId || x.owner_user_id,
          status: 'converted_to_inquiry',
          notes: ''
        })
      }
      ;(Array.isArray(qa.needQuotePdf) ? qa.needQuotePdf : []).forEach(addQuoteLead)
      ;(Array.isArray(qa.needSend) ? qa.needSend : []).forEach(addQuoteLead)
      ;(Array.isArray(qa.waitingSignature) ? qa.waitingSignature : []).forEach(addQuoteLead)

      // D) orders buckets (leadId)
      const ob = d.orders || {}
      const addOrderLead = (o) => {
        addLead(o.leadId, {
          leadCode: o.leadCode,
          name: o.customerName,
          phone: o.customerPhone,
          userId: o.ownerUserId || o.owner_user_id,
          status: 'order'
        })
      }
      ;(Array.isArray(ob.bmPending) ? ob.bmPending : []).forEach(addOrderLead)
      ;(Array.isArray(ob.bmRejected) ? ob.bmRejected : []).forEach(addOrderLead)
      ;(Array.isArray(ob.active) ? ob.active : []).forEach(addOrderLead)

      return Object.keys(map).map(k => map[k])
    },

    buildDocsMapFromDashboard(d){
      // Your UI expects docsMap keyed by leadId (because it uses l.id everywhere)
      const map = {}

      const mark = (x, docsOk) => {
        const lid = x && x.leadId ? String(x.leadId) : ''
        if (!lid) return
        map[lid] = {
          docsOk: !!docsOk,
          sitePhotos: 0,
          measurements: 0,
          drawings: 0,
          inquiryId: x.id,
          docsDueAt: x.docsDueAt || null,
          docsCompletedAt: x.docsCompletedAt || null
        }
      }

      ;(Array.isArray(d.inquiriesOverdue) ? d.inquiriesOverdue : []).forEach(x => mark(x, false))
      ;(Array.isArray(d.inquiriesDocsPending) ? d.inquiriesDocsPending : []).forEach(x => mark(x, false))
      ;(Array.isArray(d.readyForQuotation) ? d.readyForQuotation : []).forEach(x => mark(x, true))

      return map
    },

    buildQuotationsFromDashboard(d){
      // Minimal list for hasAnyQuotation(leadId)
      const out = []
      const qa = d.quoteActionNeeded || {}

      const pushLead = (x) => {
        if (!x || !x.leadId) return
        out.push({ leadId: x.leadId, id: x.quotationId || null })
      }

      ;(Array.isArray(qa.needQuotePdf) ? qa.needQuotePdf : []).forEach(pushLead)
      ;(Array.isArray(qa.needSend) ? qa.needSend : []).forEach(pushLead)
      ;(Array.isArray(qa.waitingSignature) ? qa.waitingSignature : []).forEach(pushLead)

      return out
    },



    buildDocsMapFromDashboard(d){
      const map = {}
      const overdue = Array.isArray(d.inquiriesOverdue) ? d.inquiriesOverdue : []
      const pending = Array.isArray(d.inquiriesPending) ? d.inquiriesPending : []
      const all = overdue.concat(pending)

      // key it by leadId so your existing UI can keep working with lead ids
      all.forEach(x => {
        const lid = String(x.leadId)
        map[lid] = {
          docsOk: (String(x.status) === 'DOCS_COMPLETE'),
          // optional counts not provided here; keep as 0
          sitePhotos: 0,
          measurements: 0,
          drawings: 0,
          inquiryId: x.id,
          docsDueAt: x.docsDueAt,
          docsCompletedAt: x.docsCompletedAt
        }
      })
      return map
    },


    async loadDocsBulk () {
      // ✅ only fetch docs status for ACTIVE inquiries (no order yet)
      const ids = (this.inquiriesFromLeads || []).map(x => x.id)
      if (!ids.length) { this.docsMap = {}; return }

      try {
        const api = this.$api
        const { data } = await api.get('/inquiries/docs_status', { params: { ids: ids.join(',') } })
        this.docsMap = data || {}
      } catch (e) {
        const api = this.$api
        const map = {}
        for (let i = 0; i < ids.length; i++) {
          const id = ids[i]
          try {
            const r = await api.get('/inquiries/' + id + '/docs_status')
            map[String(id)] = r.data || { sitePhotos: 0, measurements: 0, drawings: 0, docsOk: false }
          } catch (_) {
            map[String(id)] = { sitePhotos: 0, measurements: 0, drawings: 0, docsOk: false }
          }
        }
        this.docsMap = map
      }
    },


    async loadFollowupsSmart(params){
      try{
        const api = this.$api
        const r = await api.get('/followups', { params: params })
        this.followups = Array.isArray(r.data) ? r.data : []
      } catch(e){
        const api = this.$api
        const out = []
        for (let i=0;i<(this.leads||[]).length;i++){
          const l = this.leads[i]
          try{
            const rr = await api.get('/leads/' + l.id + '/followups')
            const arr = Array.isArray(rr.data) ? rr.data : []
            for (let j=0;j<arr.length;j++) out.push(arr[j])
          } catch(_) {}
        }
        this.followups = out
      }
    },

    // Sorting helpers
    setSort(view, key){
      if (!this.sortState[view]) this.sortState[view] = { key:key, dir:'asc' }
      const st = this.sortState[view]
      if (st.key === key) {
        st.dir = (st.dir === 'asc') ? 'desc' : 'asc'
      } else {
        st.key = key
        st.dir = 'asc'
      }
      this.sortState[view] = { key: st.key, dir: st.dir }
    },

    sortArrow(view, key){
      const st = this.sortState[view]
      if (!st) return ''
      if (st.key !== key) return ''
      return st.dir === 'asc' ? '▲' : '▼'
    },

    sortGeneric(list, view){
      const arr = (list || []).slice(0)
      const st = this.sortState[view] || { key:'updatedAt', dir:'desc' }
      const key = st.key
      const dir = st.dir === 'asc' ? 1 : -1

      arr.sort((a,b) => {
        if (key === 'updatedAt' || key === 'convertedAt') {
          const ta = new Date((a && a[key]) || 0).getTime()
          const tb = new Date((b && b[key]) || 0).getTime()
          return (ta - tb) * dir
        }
        const av = (a && a[key] !== undefined) ? a[key] : ''
        const bv = (b && b[key] !== undefined) ? b[key] : ''
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
      return arr
    },

    // Docs helpers
    getDoc(id, key){
      const st = (this.docsMap||{})[String(id)] || {}
      return st[key] || 0
    },

    deadline(l){
      if (!l || !l.convertedAt) return null
      return dayjs(l.convertedAt).add(this.DOCS_SLA_DAYS, 'day').toDate()
    },

    isOverdue(l){
      const dl = this.deadline(l)
      if (!dl) return false
      const st = (this.docsMap||{})[String(l.id)]
      if (st && st.docsOk) return false
      return Date.now() > dl.getTime()
    },

    // Followup helpers
    toYMD(v){
      if (!v) return ''
      const s = String(v)
      if (s.length >= 10 && s[4] === '-' && s[7] === '-') return s.slice(0,10)
      const d = new Date(v)
      if (isNaN(d.getTime())) return s.slice(0,10)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2,'0')
      const dd = String(d.getDate()).padStart(2,'0')
      return yyyy + '-' + mm + '-' + dd
    },

    async markDoneById(f){
      if (!f || !f.id) return
      try{
        await this.$api.patch(`/followups/${f.id}/done`)
        await this.loadAll()
      } catch(e){
        console.error('markDone failed', e)
      }
    },


    // Bulk selection
    isOverdueSelected(x){
      const sel = this.overdueSelected || {}
      return !!sel[x.key]
    },

    toggleOverdueSelected(x){
      const sel = this.overdueSelected || {}
      if (sel[x.key]) delete sel[x.key]
      else sel[x.key] = true
      this.overdueSelected = Object.assign({}, sel)
    },

    selectAllOverdue(on){
      const sel = {}
      if (on) {
        const all = this.overdueAll || []
        for (let i=0;i<all.length;i++) sel[all[i].key] = true
      }
      this.overdueSelected = sel
    },

    async bulkMarkFollowupsDone(){
      try{
        const all = this.overdueAll || []
        const sel = this.overdueSelected || {}

        const jobs = []
        for (let i=0;i<all.length;i++){
          const x = all[i]
          if (x && x.type === 'followup' && sel[x.key] && x.followup && x.followup.id) {
            jobs.push(this.$api.patch('/leads/' + x.followup.leadId + '/followups/' + x.followup.id, { isDone: true }))
          }
        }
        if (!jobs.length) return

        await Promise.all(jobs)
        await this.loadFollowupsSmart(this.isAdmin ? { scope:'all', branchId:this.branchId || undefined, userId:this.userId || undefined } : {})
        this.selectAllOverdue(false)
      } catch(e){
        console.error('bulkMarkFollowupsDone failed', e)
      }
    },

    async bulkCreateDrafts(){
      try{
        const all = this.overdueAll || []
        const sel = this.overdueSelected || {}

        const jobs = []
        for (let i=0;i<all.length;i++){
          const x = all[i]
          if (x && x.type === 'quote' && sel[x.key]) {
            jobs.push(this.$api.post('/quotations', { leadId: x.leadId, items: [], notes: '' }))
          }
        }
        if (!jobs.length) return

        await Promise.all(jobs)
        await this.loadAll()
        this.selectAllOverdue(false)
      } catch(e){
        console.error('bulkCreateDrafts failed', e)
      }
    },

    async createDraftForInquiry(leadId){
      try{
        const res = await this.$api.post('/quotations', { leadId: leadId, items: [], notes: '' })
        const data = res && res.data ? res.data : {}
        if (data && data.error === 'draft_exists' && data.draftId){
          this.$router.push({ name:'sales-quotations', query:{ leadId: leadId, quotationId: data.draftId } })
          return
        }
        if (data && data.id){
          this.$router.push({ name:'sales-quotations', query:{ leadId: leadId, quotationId: data.id } })
        }
        await this.loadAll()
      } catch(e){
        const r = e && e.response && e.response.data ? e.response.data : null
        if (r && r.error === 'draft_exists' && r.draftId){
          this.$router.push({ name:'sales-quotations', query:{ leadId: leadId, quotationId: r.draftId } })
          return
        }
        console.error('createDraftForInquiry failed', e)
      }
    },

    hasAnyQuotation(leadId){
      const qs = this.quotations || []
      for (let i=0;i<qs.length;i++){
        if (String(qs[i].leadId) === String(leadId)) return true
      }
      return false
    },

    isOrderStuck(o){
      if (!o) return false
      const stage = String(o.stage || '').toUpperCase()
      if (stage === 'CLOSED' || stage === 'CANCELLED') return false
      const t = new Date(o.updatedAt || 0).getTime()
      if (!t) return false
      const days = (Date.now() - t) / 86400000
      return days >= this.ORDER_STUCK_DAYS
    },

    // UI tags
    tagClass(type){
      if (type === 'followup') return 't-red'
      if (type === 'docs') return 't-amber'
      if (type === 'quote') return 't-blue'
      if (type === 'order') return 't-purple'
      return 't-gray'
    },
    tagText(type){
      if (type === 'followup') return 'Follow-up'
      if (type === 'docs') return 'Docs'
      if (type === 'quote') return 'Quotation'
      if (type === 'order') return 'Order'
      return 'Other'
    },

    // Formatting
    fmtDate(d){ return d ? dayjs(d).format('YYYY-MM-DD') : '—' },
    fmtDT(d){ return d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '—' },
    num(v){ return (v === 0 || v) ? v : 0 },

    // Messaging shortcut
    goMessage(toUserId, scopeType, scopeId){
      if (!toUserId) return

      // 1) recipient
      const to = String(toUserId)

      // 2) infer branch from recipient (owner)
      const u = (Array.isArray(this.users) ? this.users : []).find(x => String(x.id) === to)
      const branchId = (u && u.branchId) ? String(u.branchId) : ''

      // 3) normalize "scopeType" coming from queues into Compose-supported values
      const s = String(scopeType || '').toLowerCase()
      let normalized = 'NONE'
      if (s === 'order') normalized = 'ORDER'
      else if (s === 'quote' || s === 'quotation') normalized = 'QUOTATION'
      else if (s === 'lead' || s === 'inquiry' || s === 'followup' || s === 'docs') normalized = 'LEAD'

      const query = {
        to,
        scopeType: normalized,
        scopeId: scopeId ? String(scopeId) : ''
      }

      // only include branchId if we actually found it
      if (branchId) query.branchId = branchId

      this.$router.push({ name: 'messages-compose', query })
    },


  }
}
</script>

<style scoped>
/* ✅ KEEPING YOUR STYLES EXACTLY AS YOU PROVIDED (unchanged) */
.page { padding: 16px; background:#fafafa; }
.title{ margin:0; font-size:20px; font-weight:800; color:#111827; }
.subtitle{ color:#6b7280; font-size:13px; margin-top:4px; }

.topbar{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
  margin-bottom:12px;
}
.topbar__right{ display:flex; gap:8px; align-items:center; }

.filters{
  display:flex;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
  margin-bottom:12px;
}

.kpis{
  display:grid;
  grid-template-columns:repeat(5, minmax(160px, 1fr));
  gap:10px;
  margin-bottom:12px;
}
@media (max-width: 1200px){
  .kpis{ grid-template-columns:repeat(2, minmax(160px, 1fr)); }
}

.kpi{
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:14px;
  padding:12px;
  box-shadow: 0 6px 18px rgba(17,24,39,0.06);
}
.kpi__label{ font-size:12px; color:#6b7280; }
.kpi__value{ font-size:22px; font-weight:900; margin-top:4px; }
.kpi__sub{ margin-top:6px; font-size:12px; color:#6b7280; }

.tabs{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
  margin-bottom:10px;
}

.tab{
  border:1px solid #e5e7eb;
  background:#fff;
  color:#111827;
  border-radius:999px;
  padding:8px 12px;
  cursor:pointer;
  font-weight:700;
  display:flex;
  align-items:center;
  gap:8px;
}
.tab.active{
  border-color:#111827;
  background:#111827;
  color:#fff;
}

.chip{
  font-size:12px;
  border-radius:999px;
  padding:2px 8px;
  border:1px solid #e5e7eb;
}
.chip--red{ background:#7f1d1d; color:#fee2e2; border-color:#7f1d1d; }
.chip--green{ background:#14532d; color:#dcfce7; border-color:#14532d; }
.chip--amber{ background:#92400e; color:#fff7ed; border-color:#92400e; }
.chip--muted{ background:#f3f4f6; color:#111827; border-color:#e5e7eb; }

.input {
  padding: 9px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
}

.btn {
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #111827;
  color: #fff;
  cursor: pointer;
  font-weight:700;
}
.btn.secondary { background: #fff; color: #111827; }
.btn.tiny{ padding:6px 10px; border-radius:9px; font-size:12px; }

.pill{
  display:inline-block;
  background:#f3f4f6;
  border:1px solid #e5e7eb;
  padding:6px 10px;
  border-radius:999px;
  font-size:12px;
  color:#111827;
  font-weight:700;
}

.card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 28px rgba(17,24,39,0.06);
}
.card__header {
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:10px;
}
.card__title { font-weight: 900; font-size:15px; }
.card__subtitle { color: #6b7280; font-size: 12px; margin-top: 2px; }
.card__body { padding: 12px 14px; }

.grid{
  display:grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap:12px;
}
@media (max-width: 1100px){
  .grid{ grid-template-columns:1fr; }
}

.badge{
  padding:4px 10px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
  font-weight:800;
}
.badge--green{ background:#14532d;color:#dcfce7;border-color:#14532d }
.badge--red{ background:#7f1d1d;color:#fee2e2;border-color:#7f1d1d }
.badge--amber{ background:#92400e;color:#fff7ed;border-color:#92400e }

.muted { color: #6b7280; }
.smallMuted{ color:#6b7280; font-size:12px; }
.empty{ padding:10px; color:#6b7280; }

.table { width: 100%; border-collapse: collapse; }
.table th, .table td {
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;
  vertical-align: top;
}
.table th.sort{ cursor:pointer; user-select:none; }
.arrow{ font-size:11px; margin-left:6px; color:#9ca3af; }

.rowClick:hover{ background:#fafafa; }
.primaryCell{ font-weight:800; color:#111827; }

.notesCell{
  display:inline-block;
  max-width: 520px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color:#374151;
}

.pillSmall{
  display:inline-block;
  padding:2px 10px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  font-weight:800;
}
.pillSmall.ok{ background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
.pillSmall.bad{ background:#fff1f2; border-color:#fecdd3; color:#9f1239; }

.queueActions{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-bottom:10px;
  align-items:center;
}

.queueList{ display:flex; flex-direction:column; gap:10px; }

.queueItem{
  display:flex;
  gap:10px;
  border:1px solid #e5e7eb;
  border-radius:12px;
  padding:10px;
  background:#fff;
}
.queueItem:hover{ border-color:#d1d5db; }

.chk{
  display:flex;
  align-items:flex-start;
  padding-top:2px;
}
.queueItem__main{ flex:1; min-width:0; }
.queueItem__side{ display:flex; align-items:flex-start; gap:8px; flex-wrap:wrap; }

.queueItem__top{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.queueItem__title{ font-weight:900; color:#111827; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.queueItem__sub{ color:#6b7280; font-size:13px; margin-top:4px; }
.queueItem__note{ color:#6b7280; font-size:13px; margin-top:6px; }

.tag{
  font-size:12px;
  font-weight:900;
  padding:2px 10px;
  border-radius:999px;
  border:1px solid #e5e7eb;
  white-space:nowrap;
}
.t-red{ background:#fff1f2; border-color:#fecdd3; color:#9f1239; }
.t-amber{ background:#fff7ed; border-color:#fdba74; color:#9a3412; }
.t-blue{ background:#eff6ff; border-color:#bfdbfe; color:#1d4ed8; }
.t-purple{ background:#faf5ff; border-color:#e9d5ff; color:#6d28d9; }
.t-gray{ background:#f3f4f6; border-color:#e5e7eb; color:#111827; }

.simpleRow{
  display:flex;
  gap:10px;
  padding:10px 0;
  border-bottom:1px solid #f3f4f6;
}
.simpleRow:last-child{ border-bottom:none; }
.simpleRow__main{ flex:1; min-width:0; }
.simpleRow__side{ display:flex; align-items:flex-start; }
.simpleRow__title{ font-weight:900; color:#111827; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.simpleRow__note{ color:#6b7280; font-size:13px; margin-top:6px; }

.table th:nth-child(1),
.table td:nth-child(1) {
  width: 130px; /* ID */
}

.table th:nth-child(2),
.table td:nth-child(2) {
  width: 120px; /* Code */
}

.table th:nth-child(3),
.table td:nth-child(3) {
  width: 200px; /* Code */
}

.table th:nth-child(4),
.table td:nth-child(4) {
  width: 180px; /* Code */
}

.table th:nth-child(5),
.table td:nth-child(5) {
  width: 180px; /* Code */
}

.table th:nth-child(6),
.table td:nth-child(6),
.table th:nth-child(7),
.table td:nth-child(7) {
  width: 120px; /* Docs / Quote */
}

/* =========================
   Missing Docs table sizing
   ========================= */
.table--docs{
  table-layout: fixed; /* IMPORTANT: makes widths actually behave */
}

.table--docs th:nth-child(1),
.table--docs td:nth-child(1){ width: 120px; } /* ID */

.table--docs th:nth-child(2),
.table--docs td:nth-child(2){ width: 220px; } /* Name */

.table--docs th:nth-child(3),
.table--docs td:nth-child(3){ width: 170px; } /* Owner */

.table--docs th:nth-child(4),
.table--docs td:nth-child(4),
.table--docs th:nth-child(5),
.table--docs td:nth-child(5),
.table--docs th:nth-child(6),
.table--docs td:nth-child(6){
  width: 120px; /* Measurements / Drawings / Site photos */
}

.table--docs th:nth-child(7),
.table--docs td:nth-child(7){ width: 140px; } /* Deadline */

.table--docs th:nth-child(8),
.table--docs td:nth-child(8){ width: 120px; } /* Overdue */

/* Notes = last column, give it the rest */
.table--docs th:nth-child(9),
.table--docs td:nth-child(9){
  width: auto;
}

/* make Notes behave nicely inside fixed layout */
.table--docs td:nth-child(9) .notesCell{
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
